import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { Alert, Button, Result, Spin, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@services/api';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { apiErrorMessage } from '@utils/apiError';
import type { FtueProgressionResponse, FtueQuery } from '@/types';
import { dayLabel } from '../bq-insights/bqMath';
import { FtueFilters } from './FtueFilters';
import { FtueProgressionBoard } from './FtueProgressionBoard';
import { filterLabel } from './ftueFormat';
import { hasCompare } from './ftueQuery';
import { ftuePageReducer, initialFtuePageState } from './ftuePageState';

const { Title, Text } = Typography;

/** 'filter' or 'filter compared with filter', for an error message naming what failed. */
function queryLabel(q: FtueQuery): string {
  if (!q.from || !q.to) return 'the default view';
  const primary = {
    from: q.from,
    to: q.to,
    version: q.version ?? 'all',
    platform: q.platform ?? 'all',
  };
  const label = filterLabel(primary);
  if (!hasCompare(q)) return label;
  return `${label} compared with ${filterLabel({
    from: q.compareFrom ?? primary.from,
    to: q.compareTo ?? primary.to,
    version: q.compareVersion ?? primary.version,
    platform: q.comparePlatform ?? primary.platform,
  })}`;
}

/**
 * Day-0 tutorial funnel, progression tiles and the Level 2-10 ladder for one filter, with
 * an optional second filter to compare with. The first request has no params, so the server
 * picks its default (the fixed default version, 0.4.4, not the current one; all platforms; from
 * 17 Oct); the controls then show the filter the server actually used. The data is rolled up every
 * 4 hours and cached by the backend, so this page does not poll.
 */
export const FtueProgressionPage: React.FC = () => {
  const [{ shown, query, failure, barKey }, dispatch] = useReducer(
    ftuePageReducer,
    initialFtuePageState
  );
  const [data, setData] = useState<FtueProgressionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(
    async (q: FtueQuery) => {
      const isLatest = beginRequest();
      setLoading(true);
      try {
        const res = await adminApi.getFtueProgression(q);
        if (!isLatest()) return;
        setData(res.data);
        const used = res.data.view.filter;
        dispatch({
          type: 'success',
          answered: {
            ...q,
            from: used.from,
            to: used.to,
            version: used.version,
            platform: used.platform,
          },
        });
      } catch (err) {
        if (!isLatest()) return;
        console.error('Failed to load FTUE & Progression', err);
        dispatch({
          type: 'failure',
          attempted: q,
          message: apiErrorMessage(err, 'Failed to load FTUE & Progression.'),
        });
      } finally {
        if (isLatest()) setLoading(false);
      }
    },
    [beginRequest]
  );

  useEffect(() => {
    void fetchData({});
  }, [fetchData]);

  const changeFilters = (q: FtueQuery) => {
    dispatch({ type: 'start', query: q });
    void fetchData(q);
  };

  if (!data) {
    if (failure) {
      return (
        <Result
          status="error"
          title="Couldn't load FTUE & Progression"
          subTitle={failure.message}
          extra={
            <Button type="primary" onClick={() => void fetchData(failure.query)} loading={loading}>
              Retry
            </Button>
          }
        />
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          FTUE &amp; Progression
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Data through {data.dataThrough ? dayLabel(data.dataThrough) : '—'} · updated every 4 hours
        </Text>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <FtueFilters key={barKey} value={query} versions={data.versions} onChange={changeFilters} />
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => void fetchData(shown ?? {})}
        >
          Refresh
        </Button>
      </div>
      {failure && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Couldn't load ${queryLabel(failure.query)}`}
          description={`${failure.message} The figures below are still for ${queryLabel(shown ?? {})}.`}
          action={
            <Button size="small" onClick={() => void fetchData(failure.query)} loading={loading}>
              Retry
            </Button>
          }
        />
      )}
      <Spin spinning={loading}>
        <FtueProgressionBoard data={data} />
      </Spin>
    </div>
  );
};
